// React bindings over the DataStore, built on `useSyncExternalStore` so re-render
// granularity comes from the store's referentially-stable snapshots: a component
// using `useQuery`/`useEntity` re-renders only when its own slice changes. No
// external state library — plain React 19.
//
// #1707 (tracker #1677) moved `useEntity`/`useQuery`/`useExtras`/`required` into
// app-base's generic `data/use-store` (parameterized by a `DataSchema`, built on
// the generic engine's own `DataStore<Schema>`); Logbook's `DataStore` (`./Store`)
// extends that engine, so every wrapper below is a one-line instantiation at
// `LogbookSchema` with no behavior of its own. `useCurrentLog`/`useProvisionalLogs`
// stay here unchanged: the two id-0 "uncommitted" log slices are a Logbook-only
// store extension the generic engine has no notion of (see `Store.ts`).
import { useCallback, useEffect, useSyncExternalStore } from "react"
import {
  required,
  useEntity as genericUseEntity,
  useExtras as genericUseExtras,
  useQuery as genericUseQuery,
  type QueryFilter as GenericQueryFilter,
} from "app-base/data/use-store"
import type { DataStore, LiveInfo } from "./Store"
import type { EntityOf, FetchIds, LogbookSchema, StoredOf } from "./Connections"
import type { SchemaEntity } from "../Types/Generated/LogbookModel/Schema"
import type { ICurrentLog, IProvisionalLog } from "../Types/LogKind"

// Mark a filter field whose id must be known for the query to mean anything:
//   useQuery(store, "Log", { dayId: required(dayTodayId) })
// While `dayTodayId` is undefined the query yields nothing instead of every log.
export { required }

// The filter the query hooks accept: an entity's own FK ids, each either a
// plain id or one marked by `required(...)`.
export type QueryFilter<K extends SchemaEntity> = GenericQueryFilter<LogbookSchema, K>;

// Subscribe to a single entity by (type, id). Re-renders only when that entity is
// upserted or removed.
export function useEntity<K extends SchemaEntity>(
  store: DataStore,
  type: K,
  id: number,
): StoredOf<K> | undefined {
  return genericUseEntity(store, type, id);
}

// Subscribe to a query (entities of `type` matching the FK `filter`, sorted by the
// connection). Re-renders only when that query's slice changes; the `filter` may
// be a fresh object each render — it is normalized to a stable key by the store.
// A field marked `required(...)` whose id is still undefined blocks the query (see
// `required`), so it yields nothing rather than the type's every row.
export function useQuery<K extends SchemaEntity>(
  store: DataStore,
  type: K,
  filter: QueryFilter<K>,
): readonly StoredOf<K>[] {
  return genericUseQuery(store, type, filter);
}

// Subscribe to a provider query's non-entity extras (view-local data that rides a
// fetch, e.g. `auditLogEdits`). Scoped by the provider's (type, ids) so it is
// unambiguous across providers. Re-renders only when a re-fetch replaces them.
export function useExtras<K extends SchemaEntity>(
  store: DataStore,
  type: K,
  ids: FetchIds<K>,
): Readonly<Record<string, unknown>> {
  return genericUseExtras(store, type, ids);
}

// Observe the metadata of live changes already owned by the store's retained entity
// subscription. This never opens another socket subscription; it only exposes the event
// that drove the store update for view-local behavior such as new-log highlighting.
export function useDataLiveEvent<K extends SchemaEntity>(
  store: DataStore,
  type: K,
  listener: (entity: EntityOf<K>, info: LiveInfo) => void,
): void {
  useEffect(
    () => store.subscribeLiveEvent(type, listener),
    [store, type, listener],
  );
}

// Subscribe to the singleton `CurrentLog` slice (the id-0 "now" entry, fed by
// `Log/Current`). Retains the live subscription for the lifetime of the mount and
// re-renders only when the current log is replaced or cleared.
export function useCurrentLog(store: DataStore): ICurrentLog | undefined {
  useEffect(() => {
    store.retainCurrentLog();
    return () => store.releaseCurrentLog();
  }, [store]);
  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store]);
  return useSyncExternalStore(subscribe, () => store.getCurrentLog());
}

// Subscribe to the `ProvisionalLog` slice (the id-0 in-progress auto-logs, fed by
// `Log/Provisional`). Retains the live subscription for the lifetime of the mount and
// re-renders only when the provisional set changes.
export function useProvisionalLogs(store: DataStore): readonly IProvisionalLog[] {
  useEffect(() => {
    store.retainProvisionalLogs();
    return () => store.releaseProvisionalLogs();
  }, [store]);
  const subscribe = useCallback((onChange: () => void) => store.subscribe(onChange), [store]);
  return useSyncExternalStore(subscribe, () => store.getProvisionalLogs());
}
